import { FetchSendTemplateMessageParams } from 'api/templates';
import {
  MassMessagingTaskLog,
  MassMessagingTaskType,
  PriorityBroadcastDocAttachment,
} from 'types/mass-messaging/tasks';
import { fetcher } from 'utils/axios';

export const fetchGetMassMessagingTasks = async (skip: number = 0, size: number = 100) => {
  try {
    const data = await fetcher('get', `/api/massMessaging/getCompanyMassMsgngTasks?size=${size}&skip=${skip}`, 'newapi');
    if (data)
      return data as {
        data: Array<MassMessagingTaskType>;
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in getMassMessagingTasks:', error);
    return null;
  }
};

export type FetchCreateUpdateMassMessagingTaskParams = {
  name: string;
  description: string | null;
  sendDate?: string | null;
  templateObject: FetchSendTemplateMessageParams | null;
  contacts: { resultUsersList: { [key: string]: any }[]; sendToBlackListed: boolean };
  sendWebHook?: boolean;
  massMessagingGroupId?: number | null;
  /** מקור רשימת נמענים: funner | excel | fireberry | priority */
  creationSource?: string;
  recipientSource?: string;
  filePath?: string | null;
  templateFileName?: string | null;
  isDynamicVars: boolean;
  closeCall?: boolean | null;
  label?: string | null;
  assignee?: string | null;
  /** צירוף מסמך מפריוריטי לכל נמען — כשהנמענים נשלפו ממסך מסמכים */
  priorityDocAttachment?: PriorityBroadcastDocAttachment | null;
};

export const fetchCreateUpdateMassMessagingTask = async (
  id: number | null,
  params: FetchCreateUpdateMassMessagingTaskParams,
): Promise<{ result: boolean; quotaError?: unknown; noRecipientsError?: string } | null> => {
  try {
    const data = await fetcher(
      'post',
      [
        `/api/massMessaging/createUpdateFunnerMassMessagingTaskContactList/${id || 'null'}`,
        {
          data: params,
        },
      ],
      'newapi',
    );
    if (data) return data as { result: boolean };
    else return null;
  } catch (error) {
    // חסימת מכסה מהשרת (422 QUOTA_EXCEEDED, אכיפת מגבלות שלב ב') — מוחזרת לקורא
    // כדי שהמסך יציג הודעה מתורגמת (resolveQuotaErrorMessage) במקום כישלון שקט.
    const body = (error as { response?: { data?: { error?: string } } })?.response?.data
      ?? (error as { error?: string });
    if (body && typeof body === 'object' && body.error === 'QUOTA_EXCEEDED') {
      return { result: false, quotaError: body };
    }
    // דחיית רשימה ריקה מהשרת (422 NO_RECIPIENTS) — מוחזרת לקורא כדי שלא תהיה כישלון שקט
    if (body && typeof body === 'object' && body.error === 'NO_RECIPIENTS') {
      return {
        result: false,
        noRecipientsError:
          (body as { message?: string }).message || 'לא ניתן ליצור תפוצה ללא נמען אחד לפחות',
      };
    }
    console.warn('Error in createMassMessagingTask:', error);
    return null;
  }
};

export const fetchDeleteMassMessagingTask = async (id: number) => {
  try {
    const data = await fetcher('post', `/api/massMessaging/deleteFunnerMassMessagingTask/${id}`, 'history');
    if (data) return data as { result: boolean };
    else return null;
  } catch (error) {
    console.warn('Error in deleteMassMessagingTask:', error);
    return null;
  }
};

export type MassMessagingTaskLogsResponse = {
  result: boolean;
  recipients: MassMessagingTaskLog[];
  message?: string;
  /** סך הנמענים — כשהשרת מחזיר pagination */
  totalCount?: number;
  total?: number;
};

const MASS_MESSAGING_LOGS_PAGE_SIZE = 500;

export const fetchGetMassMessagingTaskLogs = async (
  id: number,
  options?: { skip?: number; size?: number },
) => {
  try {
    const skip = options?.skip ?? 0;
    const size = options?.size;
    const qs =
      size != null || skip > 0
        ? `?${new URLSearchParams({
            ...(skip > 0 ? { skip: String(skip) } : {}),
            ...(size != null ? { size: String(size) } : {}),
          }).toString()}`
        : '';
    const data = await fetcher(
      'get',
      `/api/massMessaging/getMassMessagingRecipients/${id}${qs}`,
      'newapi',
    );
    if (data) return data as MassMessagingTaskLogsResponse;
    else return null;
  } catch (error) {
    console.warn('Error in getMassMessagingTaskLogs:', error);
    return null;
  }
};

/** שליפת כל רשומות הלוג — עמודים חוזרים עד שכל הנמענים נטענו (גם אם ה-UI מציג pagination). */
export async function fetchAllMassMessagingTaskLogs(taskId: number): Promise<MassMessagingTaskLog[]> {
  const merged: MassMessagingTaskLog[] = [];
  const seenIds = new Set<string>();
  let skip = 0;

  for (let page = 0; page < 2000; page += 1) {
    const data = await fetchGetMassMessagingTaskLogs(taskId, {
      skip,
      size: MASS_MESSAGING_LOGS_PAGE_SIZE,
    });
    if (!data?.result) break;

    const batch = data.recipients ?? [];
    if (batch.length === 0) break;

    for (const row of batch) {
      if (seenIds.has(row.id)) continue;
      seenIds.add(row.id);
      merged.push(row);
    }

    const total = data.totalCount ?? data.total;
    if (typeof total === 'number' && merged.length >= total) break;
    if (batch.length < MASS_MESSAGING_LOGS_PAGE_SIZE) break;

    skip += batch.length;
  }

  return merged;
}

export const fetchResendMassMessagingTaskMessage = async (id: string) => {
  try {
    const data = await fetcher('post', `/broadcasts/resendFailedBroadcastRecipient/${id}`, 'newapi');

    if (data)
      return data as {
        result: boolean;
        message?: string;
      };
    else return null;
  } catch (error) {
    console.warn('Error in resendMassMessagingTaskMessage:', error);
    return null;
  }
};

export const fetchResendFailedBroadcastTask = async (taskId: number) => {
  try {
    const data = await fetcher('post', `/api/massMessaging/resendFailedBroadcastTask/${taskId}`, 'newapi');

    if (data)
      return data as {
        result: boolean;
        message?: string;
        successCount?: number;
        errorCount?: number;
      };
    else return null;
  } catch (error) {
    console.warn('Error in resendFailedBroadcastTask:', error);
    return null;
  }
};
